import React, { useState, useEffect, useRef } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';

function AudioRecorder({ value, onChange, disabled = false, maxRecordingTime = 120, isSaving = false }) {
  const [recordingTime, setRecordingTime] = useState(0);
  const [maxTimeReached, setMaxTimeReached] = useState(false);
  const timerRef = useRef(null);
  const onChangeCalledRef = useRef(null);

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl
  } = useReactMediaRecorder({
    audio: true,
    askPermissionOnMount: true,
    blobPropertyBag: { type: 'audio/webm' },
    mediaRecorderOptions: {
      audioBitsPerSecond: 128000,
      mimeType: 'audio/webm'
    }
  });

  // Timer effect for recording duration
  useEffect(() => {
    if (status === 'recording') {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= maxRecordingTime) {
            setMaxTimeReached(true);
            stopRecording();
            return maxRecordingTime;
          }
          return newTime;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (status === 'stopped') {
        setRecordingTime(0);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [status, stopRecording, maxRecordingTime]);

  // Update parent component when audio is recorded
  useEffect(() => {
    if (mediaBlobUrl && onChange && onChangeCalledRef.current !== mediaBlobUrl) {
      onChangeCalledRef.current = mediaBlobUrl;
      onChange(mediaBlobUrl);
    }
  }, [mediaBlobUrl, onChange]);


  const handleStartRecording = () => {
    setRecordingTime(0);
    setMaxTimeReached(false);
    startRecording();
  };

  const handleStopRecording = () => {
    stopRecording();
  };

  const handleRecordAgain = () => {
    clearBlobUrl();
    setRecordingTime(0);
    setMaxTimeReached(false);
    onChangeCalledRef.current = null; // Reset ref to allow new recording
    if (onChange) {
      onChange('');
    }
  };


  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRemainingTime = () => {
    return maxRecordingTime - recordingTime;
  };

  // Get border color based on status
  const getBorderColor = () => {
    if (status === 'recording') return 'border-red-500';
    if (mediaBlobUrl) return 'border-[#33C6B6]';
    return 'border-gray-300';
  };

  return (
    <div className="w-full">
      {/* Audio Display with Button Inside */}
      <div className="w-full">
        <div className={`relative w-full h-52 rounded-lg border ${getBorderColor()} bg-gray-50 p-4 flex flex-col justify-between`}>
          {!mediaBlobUrl || isSaving ? (
            <>
              {/* Content Area */}
              <div className="flex-1 flex items-center justify-center">
                {status === 'recording' ? (
                  <div className="flex flex-col items-center text-gray-600">
                    <div className="w-4 h-4 bg-red-500 rounded-full mb-3 animate-pulse"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-800 mb-1">
                        {formatTime(recordingTime)}
                      </div>
                      <div className="text-sm text-gray-600">
                        Remaining: {formatTime(getRemainingTime())}
                      </div>
                      {maxTimeReached && (
                        <p className="text-orange-600 text-sm mt-2">
                          Maximum recording time reached
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-center">
                    <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    <p>Ready to record your audio answer</p>
                  </div>
                )}
              </div>
              
              {/* Button Area */}
              <div className="flex justify-center mt-2 mb-4">
                {status !== 'recording' && !isSaving ? (
                  <button 
                    type="button"
                    onClick={handleStartRecording}
                    disabled={disabled || status === 'acquiring_media'}
                    className={`px-6 py-3 text-white font-medium rounded-lg transition-colors duration-200 shadow-md flex items-center ${
                      disabled || status === 'acquiring_media' ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'hover:opacity-90'
                    }`}
                    style={{ backgroundColor: disabled || status === 'acquiring_media' ? undefined : '#33C6B6' }}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    {status === 'acquiring_media' ? 'Getting Microphone...' : 'Record Answer'}
                  </button>
                ) : (
                  <button 
                    type="button"
                    onClick={handleStopRecording}
                    disabled={disabled || isSaving}
                    className={`px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg hover:from-red-600 hover:to-red-700 transition-colors duration-200 shadow-md flex items-center ${
                      disabled || isSaving ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 10a1 1 0 011-1h4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                        </svg>
                        Stop Recording
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Audio Player Area */}
              <div className="flex-1 flex items-center justify-center">
                <audio 
                  src={mediaBlobUrl}
                  controls 
                  className="w-full max-w-md"
                  preload="metadata"
                />
              </div>
              
              {/* Button Area */}
              <div className="flex justify-center mt-2 mb-4">
                {isSaving ? (
                  <button 
                    type="button"
                    disabled={true}
                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg transition-colors duration-200 shadow-md flex items-center opacity-50 cursor-not-allowed"
                  >
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </button>
                ) : (
                  <button 
                    type="button"
                    onClick={handleRecordAgain}
                    disabled={disabled}
                    className={`px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-lg hover:from-gray-600 hover:to-gray-700 transition-colors duration-200 shadow-md flex items-center ${
                      disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Record Again
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AudioRecorder;
